"use client";

import { useState, useEffect } from "react";
import { Icon } from "@iconify/react";

const RecentBurnEvents = ({ fullTable = false, data }) => {
  const [burnEvents, setBurnEvents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const eventsPerPage = 5;

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const sortedData = data.sort((a, b) => new Date(b.datetime) - new Date(a.datetime));
      
      if (fullTable) {
        // For the full table, we'll handle pagination
        setBurnEvents(sortedData);
      } else {
        // For the overview, just show the latest 5
        setBurnEvents(sortedData.slice(0, 5));
      }
    }
  }, [data, fullTable]);

  // Pagination logic for full table
  const totalPages = fullTable ? Math.ceil(burnEvents.length / eventsPerPage) : 1;
  const paginatedEvents = fullTable 
    ? burnEvents.slice((currentPage - 1) * eventsPerPage, currentPage * eventsPerPage) 
    : burnEvents;

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatNumber = (num) => {
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
    return parseFloat(num).toLocaleString();
  };

  const getBurnType = (burnType) => {
    if (burnType === 'ecosystem') return { type: "Ecosystem", color: "text-green-400", icon: "ph:tree-evergreen-bold" };
    if (burnType === 'node_rewards') return { type: "Node Rewards", color: "text-blue-400", icon: "ph:database-bold" };
    return { type: "Other", color: "text-purple-400", icon: "ph:circles-three-plus-bold" };
  };

  const truncateAddress = (address) => {
    if (!address) return 'N/A';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  if (!data) {
    return (
      <div className="bg-[#161a23] rounded-xl border border-[#2a2f38] p-6">
        <div className="animate-pulse">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-6 bg-[#23272e] rounded w-40"></div>
          </div>
          <div className="space-y-4">
            {[...Array(fullTable ? 8 : 3)].map((_, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-[#0f1419] rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 bg-[#23272e] rounded-full"></div>
                  <div>
                    <div className="h-4 bg-[#23272e] rounded w-24 mb-1"></div>
                    <div className="h-3 bg-[#23272e] rounded w-16"></div>
                  </div>
                </div>
                <div className="h-4 bg-[#23272e] rounded w-20"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#161a23] rounded-xl border border-[#2a2f38] p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white mb-1">
            {fullTable ? "All Burn Events" : "Recent Burn Events"}
          </h3>
          <p className="text-sm text-gray-400">
            {fullTable ? "Complete history of token burn events" : "Latest token burning activities"}
          </p>
        </div>
        <Icon icon="ph:clock-countdown-bold" className="text-xl text-metaxseed-blue" />
      </div>

      {burnEvents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Icon icon="ph:clock-duotone" className="text-4xl text-gray-600 mb-4" />
          <p className="text-gray-400">No burn events found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Table Header - only show in full table mode */}
          {fullTable && (
            <div className="hidden md:grid md:grid-cols-6 gap-4 px-3 py-2 text-xs font-medium text-gray-400 border-b border-[#2a2f38]">
              <div>Date & Time</div>
              <div>Amount Burned</div>
              <div>Type</div>
              <div>From Wallet</div>
              <div>Transaction</div>
              <div>Token</div>
            </div>
          )}

          {/* Events List */}
          <div className={`space-y-2 ${fullTable ? 'min-h-[340px]' : ''}`}>
            {paginatedEvents.map((event, index) => {
              const burnType = getBurnType(event.burn_type);
              
              return (
                <div key={index} className="p-3 bg-[#0f1419] rounded-lg hover:bg-[#1a1f2e] transition-colors">
                  {fullTable ? (
                    <>
                      {/* Desktop View */}
                      <div className="hidden md:grid md:grid-cols-6 gap-4 items-center">
                        <div className="text-sm text-white">
                          <div>{formatDate(event.datetime)}</div>
                          <div className="text-xs text-gray-400">{formatTime(event.datetime)}</div>
                        </div>
                        <div className="text-sm font-semibold text-white">
                          {formatNumber(event.burn_amount)}
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon icon={burnType.icon} className={`text-sm ${burnType.color}`} />
                          <span className={`text-xs ${burnType.color}`}>{burnType.type}</span>
                        </div>
                        <div className="text-sm text-gray-300">
                          {truncateAddress(event.from_wallet)}
                        </div>
                        <div className="text-sm text-blue-400">
                          <a 
                            href={`https://snowtrace.io/tx/${event.trx}?chainid=43114`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="hover:underline"
                          >
                            {truncateAddress(event.trx)}
                          </a>
                        </div>
                        <div className="text-sm text-gray-300">
                          {event.token_name}
                        </div>
                      </div>

                      {/* Mobile View */}
                      <div className="md:hidden space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon icon={burnType.icon} className={`text-sm ${burnType.color}`} />
                            <span className={`text-xs ${burnType.color}`}>{burnType.type}</span>
                          </div>
                          <div className="text-xs text-gray-400">
                            {formatDate(event.datetime)} {formatTime(event.datetime)}
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-white">Amount:</div>
                          <div className="text-sm font-semibold text-white">{formatNumber(event.burn_amount)} {event.token_name}</div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-white">From:</div>
                          <div className="text-sm text-gray-300">{truncateAddress(event.from_wallet)}</div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-white">Tx:</div>
                          <div className="text-sm text-blue-400">
                            <a 
                              href={`https://snowtrace.io/tx/${event.trx}?chainid=43114`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {truncateAddress(event.trx)}
                            </a>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 bg-red-500/20 rounded-full flex items-center justify-center">
                          <Icon icon="ph:fire-bold" className="text-sm text-red-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">
                            {formatNumber(event.burn_amount)} {event.token_name} burned
                          </p>
                          <p className="text-xs text-gray-400">
                            {formatDate(event.datetime)} at {formatTime(event.datetime)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2">
                          <Icon icon={burnType.icon} className={`text-sm ${burnType.color}`} />
                          <span className={`text-xs ${burnType.color}`}>{burnType.type}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          From: {truncateAddress(event.from_wallet)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination for full table */}
          {fullTable && totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 pt-4 border-t border-[#2a2f38]">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="w-full sm:w-auto px-4 py-2 text-sm text-white bg-metaxseed-blue rounded-md disabled:bg-gray-600 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-400">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="w-full sm:w-auto px-4 py-2 text-sm text-white bg-metaxseed-blue rounded-md disabled:bg-gray-600 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}

          {/* Summary Stats for non-full table */}
          {!fullTable && burnEvents.length > 0 && (
            <div className="mt-6 pt-4 border-t border-[#2a2f38]">
              <div className="flex justify-center text-center">
                <div>
                  <p className="text-xs text-gray-400">Total Events</p>
                  <p className="text-sm font-semibold text-white">{burnEvents.length}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RecentBurnEvents; 